import { Component, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { IFaqDto, IFaqGroupDto, IFaqGroupItemDto } from '../../interfaces/Faq/ifaq-dto';
import { FaqService } from '../../services/faq.service';
import { SharedService } from '../../services/shared.service';

declare var bootstrap: any;

@Component({
  selector: 'app-faq-management',
  templateUrl: './faq.component.html',
  styleUrls: ['./faq.component.css']
})
export class FaqManagementComponent implements OnInit {
  faqs: IFaqDto[] = [];
  loading = false;
  isEditing = false;
  timestamp = new Date().getTime();
  safeYouTubeUrl: SafeResourceUrl | null = null;
  
  selectedFaqId: number | null = null;
  
  currentFaq: Partial<IFaqDto> = {};
  currentGroup: Partial<IFaqGroupDto> = {};
  currentItem: Partial<IFaqGroupItemDto> = {};

  showLoadingMessage: boolean;
  showFileManager = false;

  constructor(private faqService: FaqService, private sharedService: SharedService, private sanitizer: DomSanitizer) { }

  ngOnInit() {
    this.sharedService.reloadUserPrivileges();
    this.loadFaqs();
  }

  toggleFileManager() {
    this.showFileManager = !this.showFileManager;
  }


  loadFaqs() {
    this.loading = true;
    this.faqService.getFaqs().subscribe(data => {
      this.faqs = data;
      this.loading = false;
      this.showLoadingMessage = false;
      if (this.faqs.length > 0 && this.selectedFaqId === null) {
        this.selectedFaqId = this.faqs[0].id;
      }
    }, err => {
      this.loading = false;
      this.showLoadingMessage = false;
    });
  }

  get selectedFaq() {
    return this.faqs.find(f => f.id === this.selectedFaqId);
  }

  // FAQ Operations
  onCreateFaq() {
    this.isEditing = false;
    let nextOrder = 0;
    if (this.faqs && this.faqs.length > 0) {
      nextOrder = Math.max(...this.faqs.map(f => f.displayOrder)) + 1;
    }
    this.currentFaq = { id: 0, isActive: true, displayOrder: nextOrder };
    this.openModal('faqModal');
  }

  onEditFaq(faq: IFaqDto) {
    this.isEditing = true;
    this.currentFaq = { ...faq };
    this.openModal('faqModal');
  }


  saveFaq() {
    if (this.currentFaq.title) this.currentFaq.title = this.currentFaq.title.trim();
    if (this.currentFaq.code) this.currentFaq.code = this.currentFaq.code.trim();

    this.faqService.saveFaq(this.currentFaq as IFaqDto).subscribe(() => {
      this.closeModal('faqModal');
      this.loadFaqs();
      this.sharedService.showSuccessMessage = true;
    });
  }

  // Group Operations
  onCreateGroup(faqId: number) {
    this.isEditing = false;
    let nextOrder = 0;
    if (this.selectedFaq && this.selectedFaq.faqGroups && this.selectedFaq.faqGroups.length > 0) {
      nextOrder = Math.max(...this.selectedFaq.faqGroups.map(g => g.displayOrder)) + 1;
    }
    this.currentGroup = { id: 0, faqId: faqId, isActive: true, displayOrder: nextOrder };
    this.openModal('groupModal');
  }

  onEditGroup(group: IFaqGroupDto) {
    this.isEditing = true;
    this.currentGroup = { ...group };
    this.openModal('groupModal');
  }


  saveGroup() {
    if (this.currentGroup.title) this.currentGroup.title = this.currentGroup.title.trim();

    this.faqService.saveFaqGroup(this.currentGroup as IFaqGroupDto).subscribe(() => {
      this.closeModal('groupModal');
      this.loadFaqs();
      this.sharedService.showSuccessMessage = true;
    });
  }

  // Item Operations
  onCreateItem(groupId: number) {
    this.isEditing = false;
    let nextOrder = 0;
    if (this.selectedFaq && this.selectedFaq.faqGroups) {
      const group = this.selectedFaq.faqGroups.find(g => g.id === groupId);
      if (group && group.faqGroupItems && group.faqGroupItems.length > 0) {
        nextOrder = Math.max(...group.faqGroupItems.map(i => i.displayOrder)) + 1;
      }
    }
    this.currentItem = { id: 0, faqGroupId: groupId, isActive: true, displayOrder: nextOrder, showSeparator: false };
    this.openModal('itemModal');
  }

  onEditItem(item: IFaqGroupItemDto) {
    this.isEditing = true;
    this.currentItem = { ...item };
    this.updateSafeYouTubeUrl();
    this.openModal('itemModal');
  }

  updateSafeYouTubeUrl() {
    if (this.currentItem.videoLink && this.isYouTubeLink(this.currentItem.videoLink)) {
      this.safeYouTubeUrl = this.getYouTubeEmbedUrl(this.currentItem.videoLink);
    } else {
      this.safeYouTubeUrl = null;
    }
  }

  isYouTubeLink(url: string): boolean {
    if (!url) return false;
    return url.includes('youtube.com') || url.includes('youtu.be');
  }

  private getYouTubeEmbedUrl(url: string): SafeResourceUrl {
    let videoId = '';
    try {
      if (url.includes('youtube.com/watch?v=')) {
        videoId = url.split('v=')[1].split('&')[0];
      } else if (url.includes('youtu.be/')) {
        videoId = url.split('youtu.be/')[1].split('?')[0];
      } else if (url.includes('youtube.com/embed/')) {
        videoId = url.split('embed/')[1].split('?')[0];
      }
    } catch (e) {
      console.error('Error parsing YouTube URL:', url, e);
    }
    return this.sanitizer.bypassSecurityTrustResourceUrl(`https://www.youtube.com/embed/${videoId}`);
  }


  saveItem() {
    if (this.currentItem.title) this.currentItem.title = this.currentItem.title.trim();
    if (this.currentItem.previewLink) this.currentItem.previewLink = this.currentItem.previewLink.trim();
    if (this.currentItem.documentLink) this.currentItem.documentLink = this.currentItem.documentLink.trim();
    if (this.currentItem.buttonTitle) this.currentItem.buttonTitle = this.currentItem.buttonTitle.trim();
    if (this.currentItem.buttonDesc) this.currentItem.buttonDesc = this.currentItem.buttonDesc.trim();
    if (this.currentItem.videoLink) this.currentItem.videoLink = this.currentItem.videoLink.trim();
    if (this.currentItem.videoDesc) this.currentItem.videoDesc = this.currentItem.videoDesc.trim();
    if (this.currentItem.videoTitle) this.currentItem.videoTitle = this.currentItem.videoTitle.trim();

    this.faqService.saveFaqGroupItem(this.currentItem as IFaqGroupItemDto).subscribe(() => {
      this.closeModal('itemModal');
      this.loadFaqs();
      this.sharedService.showSuccessMessage = true;
    });
  }

  // Modal helpers
  openModal(modalId: string) {
    const modalElement = document.getElementById(modalId);
    const modal = new bootstrap.Modal(modalElement);
    modal.show();
  }

  closeModal(modalId: string) {
    const modalElement = document.getElementById(modalId);
    const modal = bootstrap.Modal.getInstance(modalElement);
    if (modal) {
      modal.hide();
    }
  }

  // Reordering
  moveGroupUp(index: number) {
    if (index > 0 && this.selectedFaq) {
      this.swap(this.selectedFaq.faqGroups, index, index - 1);
      this.updateDisplayOrders(this.selectedFaq.faqGroups);
    }
  }

  moveGroupDown(index: number) {
    if (this.selectedFaq && index < this.selectedFaq.faqGroups.length - 1) {
      this.swap(this.selectedFaq.faqGroups, index, index + 1);
      this.updateDisplayOrders(this.selectedFaq.faqGroups);
    }
  }

  moveItemUp(group: IFaqGroupDto, index: number) {
    if (index > 0) {
      this.swap(group.faqGroupItems, index, index - 1);
      this.updateDisplayOrders(group.faqGroupItems);
    }
  }

  moveItemDown(group: IFaqGroupDto, index: number) {
    if (index < group.faqGroupItems.length - 1) {
      this.swap(group.faqGroupItems, index, index + 1);
      this.updateDisplayOrders(group.faqGroupItems);
    }
  }

  private swap(arr: any[], i: number, j: number) {
    const temp = arr[i];
    arr[i] = arr[j];
    arr[j] = temp;
  }

  private updateDisplayOrders(arr: any[]) {
    arr.forEach((item, index) => {
      item.displayOrder = index;
    });
  }

  onSaveOrders() {
    if (!this.selectedFaq) return;
    this.loading = true;
    
    // Save groups order
    this.faqService.saveFaqGroups(this.selectedFaq.faqGroups).subscribe(() => {
      // Collect all items from all groups to save in one go if possible, 
      // but my backend SaveFaqGroupItems saves a list for one group ideally (actually it just takes a list and updates by ID).
      // So I can just send ALL items from ALL groups in one list.
      
      const allItems: IFaqGroupItemDto[] = [];
      this.selectedFaq.faqGroups.forEach(g => {
        if (g.faqGroupItems) {
          allItems.push(...g.faqGroupItems);
        }
      });

      if (allItems.length > 0) {
        this.faqService.saveFaqGroupItems(allItems).subscribe(() => {
          this.finishOrderSave();
        });
      } else {
        this.finishOrderSave();
      }
    }, err => {
      this.loading = false;
    });
  }

  private finishOrderSave() {
    this.loading = false;
    this.sharedService.showTempSuccessMessage();
    this.loadFaqs();
  }
}
